/**
 * c-image-popup — 画像ポップアップ + 左端ミニタブ
 *
 * 状態遷移 (localStorage キー: c-image-popup:{sectionId}):
 *   (なし)                     → 初回。apparition-delay 秒後にポップアップ表示
 *   { state: 'minimized' }     → ポップアップ×後。ミニタブのみ表示、クリックで再表示
 *   { state: 'dismissed',
 *     hideUntil: timestamp }   → ミニタブ×後。hideUntil まで何も表示しない
 *
 * テーマエディタ (designMode) ではセクション選択で表示・選択解除でミニタブ化し、
 * localStorage には書き込まない。
 *
 * ポップアップはノンモーダル: 表示中も背景ページのスクロール・操作をブロックしない
 * (body への overflow-hidden 付与や trapFocus は行わない)。
 */
if (!customElements.get('c-image-popup')) {
  customElements.define(
    'c-image-popup',
    class CImagePopup extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('.c-image-popup__modal');
        this.dialog = this.querySelector('.c-image-popup__dialog');
        this.minitab = this.querySelector('.c-image-popup__minitab');
        this.minitabOpen = this.querySelector('.c-image-popup__minitab-open');
        if (!this.modal || !this.minitab || !this.minitabOpen) return;

        this.querySelector('.c-image-popup__close').addEventListener('click', () => this.minimize());
        this.querySelector('.c-image-popup__overlay').addEventListener('click', () => this.minimize());
        this.minitabOpen.addEventListener('click', () => this.open(true));
        this.querySelector('.c-image-popup__minitab-close').addEventListener('click', () => this.dismiss());
        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE' && !this.modal.hidden) this.minimize();
        });

        if (window.Shopify && window.Shopify.designMode) {
          document.addEventListener('shopify:section:select', (event) => {
            if (event.target.contains(this)) this.open();
          });
          document.addEventListener('shopify:section:deselect', (event) => {
            if (event.target.contains(this)) this.minimize();
          });
        } else {
          this.applyStoredState();
        }
      }

      disconnectedCallback() {
        if (this.appearTimer) clearTimeout(this.appearTimer);
      }

      get apparitionDelay() {
        return parseInt(this.dataset.apparitionDelay || '0', 10) * 1000;
      }

      get hideDays() {
        return parseInt(this.dataset.hideDays || '0', 10);
      }

      get storageKey() {
        return `c-image-popup:${this.dataset.sectionId || 'default'}`;
      }

      readState() {
        try {
          return JSON.parse(localStorage.getItem(this.storageKey));
        } catch (e) {
          return null;
        }
      }

      writeState(state) {
        if (window.Shopify && window.Shopify.designMode) return;
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (e) {
          /* localStorage 無効環境では毎回初回表示になる (安全側) */
        }
      }

      clearState() {
        try {
          localStorage.removeItem(this.storageKey);
        } catch (e) {
          /* noop */
        }
      }

      applyStoredState() {
        const state = this.readState();
        if (state && state.state === 'dismissed') {
          if (Date.now() < (state.hideUntil || 0)) return;
          this.clearState();
        } else if (state && state.state === 'minimized') {
          this.minitab.hidden = false;
          return;
        }
        this.appearTimer = setTimeout(() => this.open(), this.apparitionDelay);
      }

      /* focusDialog はミニタブ等のユーザー操作起点でのみ true にする
         (自動表示でフォーカスを奪うと閲覧・入力を中断させるため) */
      open(focusDialog = false) {
        this.minitab.hidden = true;
        this.modal.hidden = false;
        if (focusDialog && this.dialog) this.dialog.focus();
      }

      closeModal() {
        this.modal.hidden = true;
      }

      minimize() {
        if (this.appearTimer) clearTimeout(this.appearTimer);
        this.closeModal();
        this.minitab.hidden = false;
        this.minitabOpen.focus();
        this.writeState({ state: 'minimized' });
      }

      dismiss() {
        this.closeModal();
        this.minitab.hidden = true;
        this.writeState({
          state: 'dismissed',
          hideUntil: Date.now() + this.hideDays * 86400 * 1000,
        });
      }
    }
  );
}
